import { AgentSessionLabel } from '../../../api/agent-miner-client/agent-miner-client.type';
import type {
  ModelProvider,
  TaskFitCell,
  TaskFitColumn,
  TaskFitDimension,
  TaskFitRow,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { primaryLightest, redLightest, skyLighter, yellowLightest } from '../../../styles/design-tokens';
import {
  EM_DASH,
  UNKNOWN_MODEL_FAMILY,
  fmtMetric,
  reworkTone,
  reworkToneColor,
} from '../model-comparison/model-comparison.helpers';
import { normalizeProvider } from '../model-comparison/model-comparison.type';
import type { ReworkTone } from '../model-comparison/model-comparison.type';
import { FLORA_LABEL_DISPLAY_NAMES } from './task-fit.type';
import type { MatrixRow, TaskFitMetric } from './task-fit.type';

/** Known Flora-label values, so a raw `row_key` can be narrowed to the enum. */
const FLORA_LABEL_VALUES = new Set<string>(Object.values(AgentSessionLabel));

/** The three client-side filters that decide which model rows the matrix shows. */
type TaskFitColumnFilters = {
  providers: ModelProvider[];
  hiddenFamilies: Set<string>;
  showUnknown: boolean;
};

/**
 * Resolves a task type's display label. When grouping by Flora label and the
 * `row_key` is a recognized {@link AgentSessionLabel}, the friendly enum name is
 * used so the matrix reads like the Sessions tab; otherwise the backend-provided
 * `label` is trusted verbatim (raw task types, or labels this build doesn't know).
 * In the transposed matrix this drives the task-type COLUMN headers.
 *
 * @param {Pick<TaskFitRow, 'row_key' | 'label'>} row - The row (or derived recommendation) to label
 * @param {TaskFitDimension} dimension - The active grouping dimension
 * @returns {string} The label to render
 */
const resolveRowLabel = (row: Pick<TaskFitRow, 'row_key' | 'label'>, dimension: TaskFitDimension): string => {
  if (dimension === 'flora_label' && FLORA_LABEL_VALUES.has(row.row_key)) {
    return FLORA_LABEL_DISPLAY_NAMES[row.row_key as AgentSessionLabel];
  }
  return row.label;
};

/**
 * Narrows the model-family columns to the ones the matrix should show, composing
 * the three filters: the provider filter (empty means "all providers"), the
 * per-model hidden set, and the hide-Unknown toggle. A column survives only when
 * it passes every active filter (∩), mirroring how Model Comparison composes the
 * same controls.
 *
 * @param {TaskFitColumn[]} columns - The payload's model-family columns
 * @param {TaskFitColumnFilters} filters - The active client-side filters
 * @returns {TaskFitColumn[]} The columns that pass every filter
 */
const filterTaskFitColumns = (
  columns: TaskFitColumn[],
  { providers, hiddenFamilies, showUnknown }: TaskFitColumnFilters,
): TaskFitColumn[] => {
  const providerSet = new Set(providers);
  return columns.filter((column) => {
    if (!showUnknown && column.model_family === UNKNOWN_MODEL_FAMILY) return false;
    if (hiddenFamilies.has(column.model_family)) return false;
    if (providerSet.size > 0 && !providerSet.has(normalizeProvider(column.provider))) return false;
    return true;
  });
};

/**
 * Transposes the payload into {@link MatrixRow}s: one row per MODEL FAMILY,
 * sorted ALPHABETICALLY by `model_family`, each carrying every task type's cell
 * keyed by that task type's `row_key`. Cells are matched by their own
 * `model_family`, tolerating any ordering the payload sends, and a pairing the
 * payload doesn't carry becomes `null`.
 *
 * @param {TaskFitColumn[]} columns - The VISIBLE model-family columns (already filtered)
 * @param {TaskFitRow[]} rows - The payload's task-type rows (dense per column)
 * @returns {MatrixRow[]} One transposed row per model family, alphabetized
 */
const buildMatrixRows = (columns: TaskFitColumn[], rows: TaskFitRow[]): MatrixRow[] => {
  const sortedColumns = [...columns].sort((a, b) => a.model_family.localeCompare(b.model_family));
  return sortedColumns.map((column) => {
    const built: MatrixRow = { model_family: column.model_family, provider: column.provider };
    for (const row of rows) {
      built[row.row_key] = row.cells.find((cell) => cell.model_family === column.model_family) ?? null;
    }
    return built;
  });
};

/**
 * Reads the cell a given task-type column addresses out of a transposed model
 * row, returning null when the row carries no shown entry for that task type (a
 * sparse-payload guard).
 *
 * @param {MatrixRow} row - A transposed matrix row (a model family)
 * @param {string} rowKey - The task-type column's `row_key`
 * @returns {TaskFitCell | null} The cell, or null when absent
 */
const getCell = (row: MatrixRow, rowKey: string): TaskFitCell | null => {
  const value = row[rowKey];
  return value && typeof value === 'object' ? (value as TaskFitCell) : null;
};

/**
 * Recomputes, per task-type COLUMN, which VISIBLE model family is confidently
 * the best — the cell to ring. Deliberately ignores the backend `is_best` flag:
 * filtering changes the visible set, so the best must be derived from exactly
 * the model families currently on screen. Only cells with a shown rate are
 * eligible (not suppressed, non-null).
 *
 * When every eligible cell carries a 95% Wilson interval (the current backend),
 * the ranking is confidence-aware, mirroring the backend's own `is_best` logic:
 * the candidate is the cell with the lowest interval UPPER bound (not the
 * lowest raw rate, which lets a lucky low-sample cell beat a well-evidenced
 * one), and it is ringed only when its interval sits fully below every visible
 * rival's — overlapping intervals mean the data cannot pick a winner, so the
 * column gets no ring. A payload without bounds falls back to the legacy
 * lowest-raw-rate ranking. Ties break alphabetically by `model_family` so the
 * choice is stable regardless of payload order.
 *
 * The winner is dropped when even it sits in the high/"red" rework band
 * ({@link reworkTone} `bad`): a column where the best available model still
 * reworks heavily gets NO ring rather than marketing a bad cell as "best".
 *
 * @param {TaskFitColumn[]} visibleColumns - The model families currently shown
 * @param {TaskFitRow[]} rows - The payload's task-type rows
 * @returns {Map<string, string>} Task-type `row_key` → best visible `model_family`
 */
const computeBestByTaskType = (visibleColumns: TaskFitColumn[], rows: TaskFitRow[]): Map<string, string> => {
  const visibleFamilies = new Set(visibleColumns.map((column) => column.model_family));
  const bestByTaskType = new Map<string, string>();

  for (const row of rows) {
    const eligibleCells = row.cells.filter(
      (cell) => visibleFamilies.has(cell.model_family) && !cell.suppressed && cell.rework_rate_pct !== null,
    );
    if (eligibleCells.length === 0) continue;

    // Confidence-aware only when EVERY eligible cell carries bounds; a stale
    // payload (deployed ahead of the backend) keeps the legacy raw ranking.
    const hasBounds = eligibleCells.every(
      (cell) => cell.rework_rate_lower_bound_pct != null && cell.rework_rate_upper_bound_pct != null,
    );
    const rankValue = (cell: TaskFitCell): number =>
      (hasBounds ? cell.rework_rate_upper_bound_pct : cell.rework_rate_pct) as number;

    let best: TaskFitCell | null = null;
    for (const cell of eligibleCells) {
      const isLower = best === null || rankValue(cell) < rankValue(best);
      const tiesButSortsEarlier =
        best !== null && rankValue(cell) === rankValue(best) && cell.model_family.localeCompare(best.model_family) < 0;
      if (isLower || tiesButSortsEarlier) best = cell;
    }
    if (best === null) continue;

    // Separation gate: the candidate's interval must be strictly below every
    // rival's, or the column withholds judgment (vacuously true when alone).
    if (hasBounds) {
      const bestUpperBound = best.rework_rate_upper_bound_pct as number;
      const separated = eligibleCells.every(
        (cell) => cell === best || bestUpperBound < (cell.rework_rate_lower_bound_pct as number),
      );
      if (!separated) continue;
    }

    // Don't ring a "best" that is itself in the red band — a heavily-reworking
    // model isn't a recommendation, even if it's the least-bad option shown.
    if (reworkTone(best.rework_rate_pct as number) !== 'bad') bestByTaskType.set(row.row_key, best.model_family);
  }

  return bestByTaskType;
};

/**
 * Reads the value the active heatmap metric shows for a cell. Suppressed cells
 * carry no shown value under ANY metric — the sample-size floor is about
 * trusting the cell at all, not just its rework rate.
 *
 * @param {TaskFitCell} cell - The cell to read
 * @param {TaskFitMetric} metric - The active heatmap metric
 * @returns {number | null} The metric's value, or null when not shown
 */
const metricCellValue = (cell: TaskFitCell, metric: TaskFitMetric): number | null => {
  if (cell.suppressed) return null;
  switch (metric) {
    case 'cycle_time':
      return cell.median_cycle_time_days;
    case 'cost':
      return cell.cost_per_completed_task_usd;
    case 'rework':
      return cell.rework_rate_pct;
    default: {
      // Exhaustiveness check: adding a TaskFitMetric value without a case here
      // fails compilation instead of silently falling through to rework.
      const exhaustiveCheck: never = metric;
      return exhaustiveCheck;
    }
  }
};

/**
 * Buckets a value into a tone RELATIVE to the other shown values (lower is
 * better): the bottom third of the range of shown values reads good, the middle
 * neutral, the top bad. Used for the cycle-time and cost metrics, which have no
 * absolute thresholds the way rework does. All-identical values (or a single
 * value) are neutral — one number carries no relative claim.
 *
 * @param {number} value - The cell's shown value
 * @param {number[]} shownValues - Every shown value for the metric in the matrix
 * @returns {ReworkTone} The relative tone bucket
 */
const relativeTone = (value: number, shownValues: number[]): ReworkTone => {
  const distinct = shownValues.filter((shown) => Number.isFinite(shown));
  if (distinct.length < 2) return 'neutral';
  const sorted = [...distinct].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  if (min === max) return 'neutral';
  const position = (value - min) / (max - min);
  if (position <= 1 / 3) return 'good';
  if (position <= 2 / 3) return 'neutral';
  return 'bad';
};

/**
 * Resolves the fill and text colors for a matrix cell under the active metric:
 * rework uses the absolute {@link reworkTone} thresholds, while cycle time and
 * cost tone RELATIVE to the other shown values via {@link relativeTone}. A cell
 * with no shown value gets no fill so empty pairings recede.
 *
 * @param {number | null} value - The cell's shown metric value, or null
 * @param {TaskFitMetric} metric - The active heatmap metric
 * @param {number[]} shownValues - Every shown value for the metric in the matrix
 * @returns {{ background: string, color: string }} Fill and text colors
 */
const resolveMetricCellColors = (
  value: number | null,
  metric: TaskFitMetric,
  shownValues: number[],
): { background: string; color: string } => {
  if (value === null) return { background: 'transparent', color: 'inherit' };
  switch (metric) {
    case 'rework':
      return resolveCellColors(value);
    case 'cycle_time':
    case 'cost': {
      const tone = relativeTone(value, shownValues);
      return { background: reworkCellBackground(tone), color: reworkToneColor(tone) };
    }
    default: {
      // Exhaustiveness check: adding a TaskFitMetric value without a case here
      // fails compilation instead of silently toning it as rework.
      const exhaustiveCheck: never = metric;
      return exhaustiveCheck;
    }
  }
};

/**
 * Formats a cell's shown metric value for display: rework as a percentage,
 * cycle time in days, cost in dollars.
 *
 * @param {number | null} value - The cell's shown metric value, or null
 * @param {TaskFitMetric} metric - The active heatmap metric
 * @returns {string} The formatted value, or an em-dash when null
 */
const formatMetricValue = (value: number | null, metric: TaskFitMetric): string => {
  if (value === null) return EM_DASH;
  switch (metric) {
    case 'cycle_time':
      return fmtMetric(value, 'd');
    case 'cost':
      return `$${fmtMetric(value)}`;
    case 'rework':
      return fmtMetric(value, '%');
    default: {
      // Exhaustiveness check: adding a TaskFitMetric value without a case here
      // fails compilation instead of silently formatting it as a percentage.
      const exhaustiveCheck: never = metric;
      return exhaustiveCheck;
    }
  }
};

/**
 * Formats a cell's 95% Wilson interval as a compact percent range for the cell
 * tooltip (e.g. `1.8–40.4%`), or null when the cell carries no bounds
 * (suppressed, no shown rate, or a stale payload without the fields).
 *
 * @param {TaskFitCell} cell - The cell to read
 * @returns {string | null} The formatted range, or null when not available
 */
const formatReworkRange = (cell: TaskFitCell): string | null =>
  cell.rework_rate_lower_bound_pct != null && cell.rework_rate_upper_bound_pct != null
    ? `${fmtMetric(cell.rework_rate_lower_bound_pct)}–${fmtMetric(cell.rework_rate_upper_bound_pct, '%')}`
    : null;

/**
 * The value a cell should sort by under the active metric, or null when the
 * cell has no shown value (absent, suppressed, or null) — those always sink to
 * the bottom.
 *
 * @param {TaskFitCell | null} cell - The cell to read
 * @param {TaskFitMetric} metric - The active heatmap metric
 * @returns {number | null} The sortable value, or null when not shown
 */
const sortableMetricValue = (cell: TaskFitCell | null, metric: TaskFitMetric): number | null =>
  cell ? metricCellValue(cell, metric) : null;

/**
 * Sorts the transposed model rows for the shared `<Table>`'s `customSort`. The
 * model-name column (`model_family`) sorts alphabetically; a task-type column
 * (keyed by its `row_key`) sorts by that column's active metric value. Cells
 * with no shown value (absent, suppressed, or null) always sink to the BOTTOM
 * regardless of direction — the null check runs before the direction flip —
 * and tie/null groups keep a stable alphabetical order so the layout never
 * jitters.
 *
 * @param {MatrixRow[]} rows - The rows to sort (not mutated)
 * @param {keyof MatrixRow} sortBy - The active column key ('model_family' or a task-type row_key)
 * @param {'asc' | 'desc'} direction - The sort direction
 * @param {TaskFitMetric} metric - The active heatmap metric
 * @returns {MatrixRow[]} A new, sorted array
 */
const sortMatrixRows = (
  rows: MatrixRow[],
  sortBy: keyof MatrixRow,
  direction: 'asc' | 'desc',
  metric: TaskFitMetric = 'rework',
): MatrixRow[] => {
  const sign = direction === 'desc' ? -1 : 1;

  if (sortBy === 'model_family') {
    return [...rows].sort((a, b) => sign * a.model_family.localeCompare(b.model_family));
  }

  return [...rows].sort((a, b) => {
    const rateA = sortableMetricValue(getCell(a, sortBy as string), metric);
    const rateB = sortableMetricValue(getCell(b, sortBy as string), metric);

    // Nulls sink to the bottom in BOTH directions (checked before the sign flip).
    if (rateA === null && rateB === null) return a.model_family.localeCompare(b.model_family);
    if (rateA === null) return 1;
    if (rateB === null) return -1;

    if (rateA === rateB) return a.model_family.localeCompare(b.model_family);
    return sign * (rateA - rateB);
  });
};

/**
 * Buckets a rework-rate percentage into a light background token for a heatmap
 * cell fill. Reuses the shared {@link reworkTone} thresholds (LOWER-IS-BETTER),
 * so the fill polarity matches the strong text color from {@link reworkToneColor}:
 * low rework fills green, high rework fills red.
 *
 * @param {ReworkTone | null} tone - The tone bucket from reworkTone
 * @returns {string} A light design-token background color
 */
const reworkCellBackground = (tone: ReworkTone | null): string => {
  if (tone === 'good') return primaryLightest;
  if (tone === 'bad') return redLightest;
  if (tone === 'neutral') return yellowLightest;
  return skyLighter;
};

/**
 * Resolves the fill and text colors for a matrix cell. A cell with no shown rate
 * gets no fill (transparent) so empty pairings recede; a cell with a rate is
 * tinted by its rework tone with the matching strong text color.
 *
 * @param {number | null} reworkRatePct - The cell's rework rate, or null
 * @returns {{ background: string, color: string, tone: ReworkTone | null }} Fill, text color, and tone
 */
const resolveCellColors = (
  reworkRatePct: number | null,
): { background: string; color: string; tone: ReworkTone | null } => {
  if (reworkRatePct === null) {
    return { background: 'transparent', color: 'inherit', tone: null };
  }
  const tone = reworkTone(reworkRatePct);
  return { background: reworkCellBackground(tone), color: reworkToneColor(tone), tone };
};

export {
  buildMatrixRows,
  computeBestByTaskType,
  filterTaskFitColumns,
  formatMetricValue,
  formatReworkRange,
  getCell,
  metricCellValue,
  relativeTone,
  resolveCellColors,
  resolveMetricCellColors,
  resolveRowLabel,
  reworkCellBackground,
  sortMatrixRows,
};
export type { TaskFitColumnFilters };
